import ks from "../ReUsableTS/kBrowserShared";
import type { kTime } from "../ReUsableTS/kBrowserObjects";
import type { kEditableColumn } from "./kEditableColumn";
import type { kEditableTable } from "./kEditableTable";
import type { kEditableTableCell } from "./kEditableTableCell";

// Shared types, enums and small helpers of the kEditableTable grid, moved out of kEditableTable.ts (size split, 2026-10-20).
// kEditableTable.ts re-exports every exported name, so existing imports keep working.

// Minimal structural types for Intl.RelativeTimeFormat — the project's lib is only
// ES2017, which does not declare it, so a Date cell's relative-time tooltip accesses
// it through these instead of the (absent) global type. Feature-detected at the call
// site, so a runtime without it simply omits the tooltip.
export interface kRelativeTimeFormat { format(Value: number, Unit: string): string; }

export interface kRelativeTimeFormatCtor { new (Locales?: string, Options?: { numeric?: string }): kRelativeTimeFormat; }

// A Date cell's value should be a kTime, but AddEmptyRows seeds empty cells with ""
// (and some hosts seed raw values), so a Date cell's OrgValue / CurrentValue can be a
// non-kTime. Return the value only when it is a real kTime (has a ToISODate method);
// anything else ("" / undefined / string / null) becomes null. This prevents the
// "OrgValDate.ToISODate is not a function" crash in the date compare + copy paths when
// a date is first picked on a trailing empty row. Mirrors the guard already used in
// _GetSortableValue and GetGridContentsTokObjectArray.
export function AsKTimeOrNull(Val: unknown): kTime | null {
    if (ks.KErrorMode === true) { return null; }
    try {
        if (Val !== null && typeof Val === "object" && typeof (Val as kTime).ToISODate === "function") {
            return <kTime>Val;
        }
        return null;
    } catch (e) { ks.KGlobalErrorHandler(e, "kEditableTable.AsKTimeOrNull"); return null; }
}

// Natural (alphabetical + numeric) string comparator — the workspace default sort
// order. Named so it can be passed to Array.sort without an anonymous callback.
// Keeps a choice column's Options alphabetical when a new item is added.
export function kNaturalStringCompare(A: string, B: string): number {
    if (ks.KErrorMode === true) { return 0; }
    try {
        return ks.Nz(A).localeCompare(ks.Nz(B), undefined, { numeric: true, sensitivity: "base" });
    } catch (e) { ks.KGlobalErrorHandler(e, "kEditableTable.kNaturalStringCompare"); return 0; }
}

// Pure grow decision for GetCellByIndexes (exported for unit tests). A blank row
// is appended only when the requested target sits past the last existing row AND
// the grid opts into blank rows. When the caller is plain cursor NAVIGATION
// (SuppressGrowOnEmptyLastRow === true) an already-empty trailing row must NOT
// grow — otherwise Enter / ArrowDown / Tab-off-the-last-cell spawns a fresh empty
// row on every press, leaving a run of blank rows. Typing into the last row
// (LastRowHasContent === true) and paste / fill (suppress false) still grow.
export function kShouldGrowForTarget(
    TargetRowIndex: number,
    RowCount: number,
    AddBlankRows: boolean,
    SuppressGrowOnEmptyLastRow: boolean,
    LastRowHasContent: boolean
): boolean {
    if (ks.KErrorMode === true) { return false; }
    try {
        if (TargetRowIndex < RowCount) { return false; }
        if (AddBlankRows === false) { return false; }
        if (SuppressGrowOnEmptyLastRow === true && LastRowHasContent === false) { return false; }
        return true;
    } catch (e) {
        ks.KGlobalErrorHandler(e, "kEditableTable.kShouldGrowForTarget");
        return false;
    }
}

// Implemented by kEditableRowForm. The base grid opens the row form through
// this seam (kEditableTable.RowFormFactory) so it never imports the form module
// — same opt-in-by-registration pattern as the RichHtml editor factory.
export interface kRowFormHost {
    Open: () => void;
}

// A self-contained inline control the row form embeds directly (no popup, no
// extra click). The ONE shape used for every custom/inline control the form
// hosts: the embedded rich-text editor (kEditableTable.CreateRichHtmlInlineField,
// overridden by kEditableTableRichHtml to return a live kJQXEditor host) AND any
// custom column control (people picker, document link) supplied via
// kEditableColumn.FormFieldFactory. The factory/host owns the control; the form
// calls GetValue() on OK to commit via Row.SetValueToCell and Destroy() on
// close. Supplied by the same shared module that supplies the cell's
// CustomRenderer/CustomEditor, so the base grid and form stay project-agnostic.
export interface kRowFormFieldControl {
    Element: HTMLElement;
    GetValue: () => any;
    Destroy: () => void;
}

// Implemented by kEditableTableFilterUI. The grid drives the filter UI through
// this controller (built via kEditableTable.FilterUIFactory) without importing
// the UI module. Refresh re-renders the chip bar + funnel highlights after the
// model changes; OpenColumnPopup is called by the column context menu to reuse
// the same Tier-1 popup the funnel opens.
export interface kFilterUIController {
    Refresh: () => void;
    OpenColumnPopup: (Column: kEditableColumn, Anchor: HTMLElement) => void;
    OpenAdvanced: () => void;
    // Releases any listeners/DOM the UI holds; called from kEditableTable.Destroy.
    Destroy: () => void;
}

/**
 * Which grid currently owns the document-level key handler. Moved out of kEditableTable (size split)
 * so rows and cells can claim it without importing the grid at load time; kEditableTable.SetActiveTable /
 * ClearActiveTable delegate here and keep their signatures and error labels.
 */
export class kEditableTableActive {
    public static ActiveTable: kEditableTable | null = null;
    public static SetActiveTable = (Table: kEditableTable): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            kEditableTableActive.ActiveTable = Table;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableActive.SetActiveTable");
        }
    }
    // Releases the grid's claim on the document-level key handler (see kEditableTable.ClearActiveTable).
    public static ClearActiveTable = (Table: kEditableTable): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (kEditableTableActive.ActiveTable === Table) { kEditableTableActive.ActiveTable = null; }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableActive.ClearActiveTable");
        }
    }
}

/**
 * Builds the option list for a Select cell's inline editor as a NEW array (the
 * caller's configured options are never mutated). Guarantees the list contains
 * a trailing blank (so the cell can be cleared) and the cell's CURRENT value
 * (so a value outside the configured choices - e.g. a code from a wider locale
 * dataset - is faithfully represented in the <select> and therefore PRESERVED
 * on a no-change blur, instead of being silently overwritten by the dropdown's
 * fallback first option). Pure + side-effect-free, so it is unit-testable on
 * its own and protects every consumer of the grid. Root-caused 2026-06-28.
 */
export function kBuildSelectEditOptions(ConfiguredOptions: string[], CurrentValue: string): string[] {
    const Out: string[] = [];
    if (ks.KErrorMode === true) { return Out; }
    try {
        let CurrOption: string;
        for (CurrOption of ConfiguredOptions) { Out.push(CurrOption); }
        if (Out.indexOf("") === -1) { Out.push(""); }
        if (CurrentValue !== "" && Out.indexOf(CurrentValue) === -1) { Out.push(CurrentValue); }
        return Out;
    } catch (e) {
        ks.KGlobalErrorHandler(e, "kBuildSelectEditOptions");
        return Out;
    }
}

export enum kHTMLEditableColType{
    Text = 1,
    Number = 2,
    Date = 3,
    Time = 4,
    DateTime = 5,
    Checkbox = 6,
    Select = 7,
    Button = 8,
    Link = 9,
    Image = 10,
    File = 11,
    Color = 12,
    SelectMulti = 13,
    Radio = 14,
    Currency = 15, //Number formatted with currency characters
    RichHtml = 16, //HTML string; read mode paints a one-line preview, editing opens a rich-text popup (kEditableTableRichHtml)
    Geo = 17 //Geolocation JSON {Title,Lat,Long}; read mode paints "Title (lat, long)", editing opens a Mapbox map picker (kEditableTableGeoCell). The render/edit come from the column's CustomRenderer/CustomEditor (wired by kAddGeoColumn), so the base grid never imports Mapbox.
}

// Implemented by the rich HTML cell popup in kEditableTableRichHtml.ts. The
// base grid only knows how to ask an open popup to commit or cancel; creating
// one is the subclass's job via kEditableTable.OpenRichHtmlEditor.
export interface kRichHtmlCellEditorHost {
    Commit(): void;
    Cancel(): void;
}

/**
 * The presentation of ONE heat/status cell — returned by a status provider and painted
 * by kStatusCellRenderer onto the <td>. All fields optional; the colours are applied
 * INLINE `!important` so they beat the component's own `.kEditableTableCell{
 * background:white !important }` (the trap a plain CSS class hits). Use for per-cell
 * (value-driven) colouring — TintColor colours the WHOLE column, this colours each cell.
 */
export interface kCellStatus {
    Text?: string;   // in-cell label (default: the bound value)
    Title?: string;  // hover tooltip
    Bg?: string;     // background colour (inline !important)
    Fg?: string;     // text colour (inline !important)
    Class?: string;  // space-separated classes to add (stable hooks, NOT the colour)
    Align?: string;  // text-align (default "center")
}

/**
 * Build a reusable read-mode CustomRenderer for a heat/status column: the provider maps
 * a cell value to a kCellStatus, and this paints the <td> (text / tooltip / classes +
 * the colours inline-important). Assign to `col.CustomRenderer`, or — the first-class
 * way — call `col.SetStatusProvider(provider)`. Packages the DOM boilerplate every
 * per-cell heat/status column otherwise repeats (coverage heatmaps, RAG status, …).
 */
export const kStatusCellRenderer = (Provider: (value: any) => kCellStatus | null): ((value: any, cell: kEditableTableCell) => void) => {
    const Render = (Value: any, Cell: kEditableTableCell): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            const Td: HTMLTableCellElement = Cell.Cell;
            // Reset the styling this renderer manages, so a re-render to a lighter (or null)
            // status never keeps a stale colour/tooltip on a value-changing status column.
            Td.style.removeProperty("background-color");
            Td.style.removeProperty("color");
            Td.removeAttribute("title");
            const S: kCellStatus | null = Provider(Value);
            if (S === null || S === undefined) { Td.textContent = ks.Nz(Value); return; }
            if (S.Text !== undefined) { Td.textContent = S.Text; } else { Td.textContent = ks.Nz(Value); }
            if (S.Title !== undefined) { Td.title = S.Title; }
            let Align: string = "center";
            if (S.Align !== undefined && S.Align !== "") { Align = S.Align; }
            Td.style.setProperty("text-align", Align, "important");
            if (S.Class !== undefined && S.Class !== "") {
                let Part: string;
                for (Part of S.Class.split(" ")) { if (Part !== "") { Td.classList.add(Part); } }
            }
            if (S.Bg !== undefined && S.Bg !== "") { Td.style.setProperty("background-color", S.Bg, "important"); }
            if (S.Fg !== undefined && S.Fg !== "") { Td.style.setProperty("color", S.Fg, "important"); }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kStatusCellRenderer");
        }
    };
    return Render;
};

/**
 * One column, described as DATA rather than built by code. See kEditableColumns.FromSchema.
 *
 * Only `InternalName` is required. Everything else defaults to what a read-only text column of an
 * ordinary width would be, so a plain register column is one short literal and only the unusual
 * ones carry any detail.
 */
export interface IkColumnSpec {
    /** The property name on the bound kObject — becomes KObjectPropertyName. */
    InternalName: string;
    /** Header text. Falls back to InternalName when empty, which is right for a quick grid and
     *  wrong for a shipped one — a product with a translation layer always supplies this. */
    Header?: string;
    /** Hover text on the header. Where a product shortens headers to fit, this is where the full
     *  name belongs, so the short header stays unambiguous. */
    HeaderTooltip?: string;
    Type?: kHTMLEditableColType;
    Width?: number;
    Options?: string[];
    /** Defaults to the FromSchema call's `Locked` argument, so a read-only grid is one argument
     *  rather than a flag repeated on every row. */
    Locked?: boolean;
    Frozen?: boolean;
    Filterable?: boolean;
    Tab?: string;
    CssClass?: string;
    CustomRenderer?: any;
}

export enum kEdTblSelectionMode{
    NoSelection ="NoSelection",
    SingleCell = "SingleCell",

    SingleRow = "SingleRow",
    MultipleRowsExtendUp = "MultipleRowsExtendUp",
    MultipleRowsExtendDown = "MultipleRowsExtendDown",

    SingleColumn = "SingleColumn",
    MultipleColsExtendRight ="MultipleColsExtendRight",
    MultipleColsExtendLeft ="MultipleColsExtendLeft",

    CustomExtRightDown = "CustomExtRightDown",
    CustomExtRightUp ="CustomExtRightUp",
    CustomExtLeftDown = "CustomExtLeftDown",
    CustomExtLeftUp = "CustomExtLeftUp",
    SelectAll = "SelectAll"    
}

export class GridValue{
    public OriginalValue:any = null;
    public CurrentValue:any = null;
    public Dirty:boolean = false;
    public Column:kEditableColumn|null = null;
}

export class kCellEditCommand {
    private _Cell: kEditableTableCell;
    private _OldValue: any;
    private _NewValue: any;

    constructor(Cell: kEditableTableCell, OldValue: any, NewValue: any) {
        this._Cell = Cell;
        this._OldValue = OldValue;
        this._NewValue = NewValue;
    }

    public Undo = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            this._Cell.SetNewValue(this._OldValue, false);
            this._Cell.SetClean();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kCellEditCommand.Undo");
        }
    }

    public Redo = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            this._Cell.SetNewValue(this._NewValue, false);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kCellEditCommand.Redo");
        }
    }
}

export class kEditTableFillIcons{
    public static FillDown:string = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQCAYAAAAf8/9hAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAJcEhZcwAADsIAAA7CARUoSoAAAAGHaVRYdFhNTDpjb20uYWRvYmUueG1wAAAAAAA8P3hwYWNrZXQgYmVnaW49J++7vycgaWQ9J1c1TTBNcENlaGlIenJlU3pOVGN6a2M5ZCc/Pg0KPHg6eG1wbWV0YSB4bWxuczp4PSJhZG9iZTpuczptZXRhLyI+PHJkZjpSREYgeG1sbnM6cmRmPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5LzAyLzIyLXJkZi1zeW50YXgtbnMjIj48cmRmOkRlc2NyaXB0aW9uIHJkZjphYm91dD0idXVpZDpmYWY1YmRkNS1iYTNkLTExZGEtYWQzMS1kMzNkNzUxODJmMWIiIHhtbG5zOnRpZmY9Imh0dHA6Ly9ucy5hZG9iZS5jb20vdGlmZi8xLjAvIj48dGlmZjpPcmllbnRhdGlvbj4xPC90aWZmOk9yaWVudGF0aW9uPjwvcmRmOkRlc2NyaXB0aW9uPjwvcmRmOlJERj48L3g6eG1wbWV0YT4NCjw/eHBhY2tldCBlbmQ9J3cnPz4slJgLAAAAiklEQVQ4T+3SPRIBQRRF4U9O+Sk9SkJExo4tQKpsgggpeyHpKT2PwIgETlUnfe+50ePJAhsMir9IP3eWMehghzvOSLGAMU65s89Og1mW342U8gXzImuQwkgP3SBXUYqUI0cc2sg15UhruSbhits3cs00v49YYYtJDAqq3FnH4H8Hr/zOHQwxip8lDyeqOqpjI20SAAAAAElFTkSuQmCC";
    public static FillRight:string = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQCAYAAAAf8/9hAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAJcEhZcwAADsIAAA7CARUoSoAAAAGHaVRYdFhNTDpjb20uYWRvYmUueG1wAAAAAAA8P3hwYWNrZXQgYmVnaW49J++7vycgaWQ9J1c1TTBNcENlaGlIenJlU3pOVGN6a2M5ZCc/Pg0KPHg6eG1wbWV0YSB4bWxuczp4PSJhZG9iZTpuczptZXRhLyI+PHJkZjpSREYgeG1sbnM6cmRmPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5LzAyLzIyLXJkZi1zeW50YXgtbnMjIj48cmRmOkRlc2NyaXB0aW9uIHJkZjphYm91dD0idXVpZDpmYWY1YmRkNS1iYTNkLTExZGEtYWQzMS1kMzNkNzUxODJmMWIiIHhtbG5zOnRpZmY9Imh0dHA6Ly9ucy5hZG9iZS5jb20vdGlmZi8xLjAvIj48dGlmZjpPcmllbnRhdGlvbj4xPC90aWZmOk9yaWVudGF0aW9uPjwvcmRmOkRlc2NyaXB0aW9uPjwvcmRmOlJERj48L3g6eG1wbWV0YT4NCjw/eHBhY2tldCBlbmQ9J3cnPz4slJgLAAAAhklEQVQ4T83SMRIBMRiG4WcdQMWuBhWdPbXG6FxCa7UOs5owkdnJr+OrMnneSRX+YTuc0JWQdgjcESMGLAtrcKk46eWhEm0CJ13Wosj5IooczHFN0QPbwLtZEYzZucnOr5X+0SxwS9EdbY6RVzHytoZYB/7+SFOYf6QpB3ucsSohrQ/8x3sCxEw3JKoNdYEAAAAASUVORK5CYII=";
}
